// The demo user (GET /user/me) and each organization's users (GET /user/:org).

import type { UpdateUserRequest, UserSchema } from '../../types/auth.type';
import {
  DEMO_DRIVERS,
  DEMO_OUTLET_ORG_ID,
  DEMO_OUTLET_STAFF,
  DEMO_RECYCLER_MANAGERS,
  DEMO_RECYCLER_ORG_ID,
  DEMO_USER,
  daysAgo,
  iso,
} from '../core';
import { fail } from './errors';

const WILDCARD_PERMISSIONS = [`recyclee.${DEMO_OUTLET_ORG_ID}.*`, `recyclee.${DEMO_RECYCLER_ORG_ID}.*`];
const ALL_SITES = { [DEMO_OUTLET_ORG_ID]: ['*'], [DEMO_RECYCLER_ORG_ID]: ['*'] };

export function demoMe(): UserSchema {
  return {
    id: DEMO_USER.id,
    sub: DEMO_USER.id,
    username: DEMO_USER.username,
    email: DEMO_USER.email,
    data: {
      permissions: [...WILDCARD_PERMISSIONS],
      sites: { ...ALL_SITES },
      full_name: DEMO_USER.full_name,
      phone: DEMO_USER.phone,
      role: 'recycler',
    },
    created_at: iso(daysAgo(420)),
    updated_at: iso(daysAgo(2)),
  };
}

function legacyUser(
  id: string,
  username: string,
  orgId: string,
  role: string,
  extra: Record<string, unknown>,
  createdDaysAgo: number,
): UserSchema {
  return {
    id,
    sub: id,
    username,
    email: username,
    data: {
      permissions: role.endsWith('_staff')
        ? [`recyclee.${orgId}.*.pre_order.read`, `recyclee.${orgId}.*.appetite.read`]
        : [`recyclee.${orgId}.*`],
      permissions_legacy: { [orgId]: [role] },
      sites: { [orgId]: ['*'] },
      role,
      ...extra,
    },
    created_at: iso(daysAgo(createdDaysAgo)),
    updated_at: iso(daysAgo(Math.max(1, Math.floor(createdDaysAgo / 7)))),
  };
}

function baseUsers(): Record<string, UserSchema[]> {
  return {
    [DEMO_RECYCLER_ORG_ID]: [
      demoMe(),
      ...DEMO_RECYCLER_MANAGERS.map((m, i) =>
        legacyUser(m.id, m.email, DEMO_RECYCLER_ORG_ID, 'recycler_ops_manager', {
          full_name: m.full_name,
          full_name_ar: m.full_name_ar,
          phone: m.phone,
          job_title: m.job_title,
          facility_id: m.facility_id,
        }, 640 - i * 90),
      ),
      ...DEMO_DRIVERS.map((d, i) =>
        legacyUser(d.id, d.email, DEMO_RECYCLER_ORG_ID, 'recycler_staff', {
          full_name: d.full_name,
          full_name_ar: d.full_name_ar,
          phone: d.phone,
          licence_no: d.licence_no,
          licence_expiry: d.licence_expiry,
          city: d.city,
          home_facility_id: d.home_facility_id,
          status: d.status,
          job_title: 'Driver',
        }, 520 - i * 37),
      ),
    ],
    [DEMO_OUTLET_ORG_ID]: [
      demoMe(),
      ...DEMO_OUTLET_STAFF.map((s, i) =>
        legacyUser(s.id, s.email, DEMO_OUTLET_ORG_ID, s.role === 'ops_manager' ? 'waste_outlet_ops_manager' : 'waste_outlet_staff', {
          full_name: s.full_name,
          full_name_ar: s.full_name_ar,
          phone: s.phone,
          job_title: s.job_title,
          job_title_ar: s.job_title_ar,
          site_ids: s.site_ids,
        }, 700 - i * 41),
      ),
    ],
  };
}

let usersByOrg: Record<string, UserSchema[]> = {};

export function resetUsers(): void {
  usersByOrg = baseUsers();
}

export function orgUsers(orgId: string): UserSchema[] {
  return usersByOrg[orgId] ?? [];
}

export function updateOrgUser(orgId: string, userId: string, req: Partial<UpdateUserRequest>): UserSchema {
  const current = orgUsers(orgId).find((u) => u.id === userId || u.sub === userId);
  if (!current) fail(404, 'User not found');
  const next: UserSchema = {
    ...current,
    username: typeof req.username === 'string' && req.username.trim() ? req.username.trim() : current.username,
    data: { ...(current.data ?? {}), ...(req.data ?? {}) },
    updated_at: iso(new Date()),
  };
  usersByOrg = { ...usersByOrg, [orgId]: orgUsers(orgId).map((u) => (u === current ? next : u)) };
  return next;
}
